import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import * as yup from "yup"
import { Formik, Form } from "formik"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import ListingTabs from "./ListingTabs"
import SearchInput from "./SearchInput"
import FilterDropdown, { toSingleFilterValue } from "./FilterDropdown"
import ExportLogButton from "./ExportLogButton"
import Pagination from "./Pagination"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomIconButton from "../OnboardingComponents/CustomIconButton"
import CustomInput from "../OnboardingComponents/CustomInput"
import EmptyState from "../Common/EmptyState"
import TruncatedText from "../Common/TruncatedText"
import AdminCityListingTitle from "../AdminLayout/AdminCityListingTitle"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { get, patch } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import ViewRequestModal from "../../pages/Admin/Requests/ViewRequestModal"
import {
  CITY_UPDATE_REQUEST_WITH,
  mapCityUpdateRequest,
} from "../../pages/Admin/Requests/adminRequestsData"
import {
  CITY_TRANSFER_STATUS_FILTER_OPTIONS,
  CITY_TRANSFER_STATUS_TABS,
} from "../../pages/Admin/Cities/adminCityTransfersData"

const PAGE_SIZE = 10
const REASON_MAX = 250
const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
}

const canDecide = status => {
  const key = String(status || "")
    .trim()
    .toLowerCase()
  return !key || key === "pending"
}

const decisionValidationSchema = yup.object().shape({
  decision_reason: yup
    .string()
    .trim()
    .max(REASON_MAX, `Reason must be at most ${REASON_MAX} characters`),
})

const TransferDecisionModal = ({
  isOpen,
  action,
  request,
  onClose,
  onSuccess,
}) => {
  const isApprove = action === "approve"
  const title = isApprove ? "Approve Request" : "Dismiss Request"
  const confirmLabel = isApprove ? "Approve" : "Dismiss"
  const confirmVariant = isApprove ? "primary" : "danger"

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!request?.id) return
    try {
      const response = await patch(
        endpoints.updateAdminCityUpdateRequest(request.id),
        {
          status: isApprove ? "approved" : "rejected",
          action_reason: values.decision_reason?.trim() || "",
        },
      )
      if (response?.data?.success) {
        toast.success(
          response.data.message ||
            (isApprove
              ? "Transfer approved successfully."
              : "Transfer dismissed successfully."),
          TOAST_OPTIONS,
        )
        onSuccess?.()
        onClose()
      } else {
        toastApiError(response, "Failed to update transfer request")
      }
    } catch (err) {
      toastApiError(err, "Failed to update transfer request")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--admin-decision"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-decision-modal__body">
        <Formik
          key={isOpen ? `${action}-${request?.id}` : "closed"}
          initialValues={{ decision_reason: "" }}
          validationSchema={decisionValidationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="admin-decision-modal">
              <div className="admin-decision-modal__main">
                <h3 className="admin-decision-modal__title">{title}</h3>
                {request?.name ? (
                  <p className="admin-decision-modal__subtitle">
                    {isApprove ? "Approving" : "Dismissing"} transfer from{" "}
                    <strong>{request.name}</strong>
                    {request.cityChangeLabel
                      ? ` (${request.cityChangeLabel})`
                      : ""}
                  </p>
                ) : null}
                <CustomInput
                  label="Reason (optional)"
                  name="decision_reason"
                  textarea
                  rows={4}
                  placeholder="Add a reason for this decision..."
                  value={values.decision_reason}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("decision_reason", true)}
                  error={touched.decision_reason && errors.decision_reason}
                  helperText={`${values.decision_reason.length}/${REASON_MAX}`}
                />
              </div>
              <div className="admin-decision-modal__footer">
                <CustomButton
                  variant="transparent"
                  size="md"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant={confirmVariant}
                  size="md"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {confirmLabel}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

TransferDecisionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  action: PropTypes.oneOf(["approve", "reject"]),
  request: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

TransferDecisionModal.defaultProps = {
  action: "approve",
  request: null,
  onSuccess: undefined,
}

/**
 * Shared City Transfer Requests listing — same APIs as Admin Requests
 * Change City tab (`GET/PATCH /api/v1/admin/city-update-requests`), scoped by city_id.
 */
const TransfersListing = ({
  documentTitle = "City Transfer Requests | AMOK . EARTH Admin",
  cityId,
  showExport = true,
}) => {
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)
  const [viewModal, setViewModal] = useState({ isOpen: false, request: null })
  const [decisionModal, setDecisionModal] = useState({
    isOpen: false,
    action: null,
    request: null,
  })

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, statusFilter, cityId])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": CITY_UPDATE_REQUEST_WITH,
        search: debouncedSearch || "",
        status: "",
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = pageSize
      }

      if (cityId) params.city_id = cityId

      if (activeTab !== "all") {
        params.status = activeTab
      } else if (statusFilter) {
        params.status = statusFilter
      }

      return params
    },
    [activeTab, cityId, currentPage, debouncedSearch, pageSize, statusFilter],
  )

  const fetchTransfers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminCityUpdateRequests, {
        params: buildListParams(),
      })

      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapCityUpdateRequest)
        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(
          response,
          "Failed to load transfer requests",
        )
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load transfer requests")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchTransfers()
  }, [fetchTransfers])

  const openDecision = (action, request) => {
    setViewModal({ isOpen: false, request: null })
    setDecisionModal({ isOpen: true, action, request })
  }

  const closeDecision = () => {
    setDecisionModal({ isOpen: false, action: null, request: null })
  }

  const openView = request => {
    setViewModal({ isOpen: true, request })
  }

  const closeView = () => {
    setViewModal({ isOpen: false, request: null })
  }

  const handleApprove = request => {
    if (!request?.id) return false
    openDecision("approve", request)
    return false
  }

  const handleDismiss = request => {
    if (!request?.id) return
    openDecision("reject", request)
  }

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminCityUpdateRequests, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export transfer requests")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export transfer requests"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `city-transfer-requests-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Transfer requests exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export transfer requests")
    } finally {
      setIsExporting(false)
    }
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 8 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  const itemLabel =
    activeTab === "pending" ? "pending requests" : "Requests"

  return (
    <div className="admin-page admin-city-transfers">
      <div className="admin-city-transfers__toolbar">
        {cityId ? (
          <AdminCityListingTitle
            label="City Transfer Requests"
            cityId={cityId}
          />
        ) : (
          <h1 className="admin-city-transfers__title">City Transfer Requests</h1>
        )}
        <div className="admin-city-transfers__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by Member..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-city-transfers__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "status",
                type: "select",
                label: "Status",
                options: CITY_TRANSFER_STATUS_FILTER_OPTIONS,
                value: statusFilter,
                defaultValue: "",
                onChange: next => {
                  setStatusFilter(toSingleFilterValue(next))
                },
              },
            ]}
          />
          {showExport ? (
            <ExportLogButton
              entityLabel="transfers"
              onClick={handleExport}
              loading={isExporting}
            />
          ) : null}
        </div>
      </div>

      <div className="admin-city-transfers__tabs-row">
        <ListingTabs
          items={CITY_TRANSFER_STATUS_TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div
        className="admin-city-transfers__table-wrapper"
        aria-busy={isLoading || undefined}
      >
        <div className="admin-city-transfers__table" role="table">
          <div
            className="admin-city-transfers__table-row admin-city-transfers__table-row--head"
            role="row"
          >
            <div role="columnheader">Member</div>
            <div role="columnheader">Reason</div>
            <div role="columnheader">Change City to</div>
            <div role="columnheader">Date Requested</div>
            <div role="columnheader">Status</div>
            <div
              role="columnheader"
              className="admin-city-transfers__actions-head"
            >
              Actions
            </div>
          </div>

          {isLoading
            ? skeletonRows.map(key => (
                <div
                  key={key}
                  className="admin-city-transfers__table-row admin-city-transfers__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-city-transfers__skeleton" />
                  <div className="admin-city-transfers__skeleton" />
                  <div className="admin-city-transfers__skeleton" />
                  <div className="admin-city-transfers__skeleton" />
                  <div className="admin-city-transfers__skeleton" />
                  <div className="admin-city-transfers__skeleton" />
                </div>
              ))
            : rows.map(row => (
                <div
                  key={row.id}
                  className="admin-city-transfers__table-row"
                  role="row"
                >
                  <div className="admin-city-transfers__user" role="cell">
                    <Avatar
                      src={row.avatar}
                      name={row.name}
                      size="sm"
                      className="admin-city-transfers__avatar"
                    />
                    <TruncatedText className="admin-city-transfers__name">
                      {row.name}
                    </TruncatedText>
                  </div>
                  <TruncatedText
                    as="div"
                    className="admin-city-transfers__cell"
                    role="cell"
                    title={row.reason}
                  >
                    {row.reason}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-city-transfers__cell admin-city-transfers__cell--center"
                    role="cell"
                    title={row.newCity || row.cityChangeLabel}
                  >
                    {row.newCity || "--"}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-city-transfers__cell admin-city-transfers__cell--center"
                    role="cell"
                  >
                    {row.date}
                  </TruncatedText>
                  <div className="admin-city-transfers__status-cell" role="cell">
                    <span
                      className={`admin-city-transfers__status admin-city-transfers__status--${row.status}`}
                    >
                      {STATUS_LABEL[row.status] || row.statusLabel || row.status}
                    </span>
                  </div>
                  <div className="admin-city-transfers__actions" role="cell">
                    {canDecide(row.status) ? (
                      <>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          className="admin-city-transfers__approve-btn"
                          onClick={() => handleApprove(row)}
                        >
                          Approve
                        </CustomButton>
                        <CustomButton
                          variant="transparent"
                          size="sm"
                          type="button"
                          className="admin-city-transfers__reject-btn"
                          onClick={() => handleDismiss(row)}
                        >
                          Reject
                        </CustomButton>
                      </>
                    ) : (
                      <CustomButton
                        variant="transparent"
                        size="sm"
                        type="button"
                        className="admin-city-transfers__view-btn"
                        onClick={() => openView(row)}
                      >
                        View Details
                      </CustomButton>
                    )}
                    <CustomIconButton
                      iconSrc={ICONS.adminEye}
                      label={`View ${row.name}'s transfer`}
                      size="sm"
                      className="admin-city-transfers__eye-btn"
                      onClick={() => openView(row)}
                    />
                  </div>
                </div>
              ))}

          {!isLoading && rows.length === 0 ? (
            <div className="admin-city-transfers__empty">
              <EmptyState
                title={
                  error
                    ? "Couldn’t load transfer requests"
                    : "No transfer requests found"
                }
                description={
                  error || "Try adjusting your search or filters."
                }
                actionText={error ? "Try again" : undefined}
                onActionClick={error ? fetchTransfers : undefined}
              />
            </div>
          ) : null}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel={itemLabel}
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-city-transfers__pagination"
      />

      <ViewRequestModal
        isOpen={viewModal.isOpen}
        request={viewModal.request}
        onClose={closeView}
        onAccept={handleApprove}
        onDismiss={handleDismiss}
      />

      <TransferDecisionModal
        isOpen={decisionModal.isOpen}
        action={decisionModal.action}
        request={decisionModal.request}
        onClose={closeDecision}
        onSuccess={fetchTransfers}
      />
    </div>
  )
}

export default TransfersListing

TransfersListing.propTypes = {
  documentTitle: PropTypes.string,
  cityId: PropTypes.string,
  showExport: PropTypes.bool,
}

TransfersListing.defaultProps = {
  documentTitle: "City Transfer Requests | AMOK . EARTH Admin",
  cityId: undefined,
  showExport: true,
}
